import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Button } from "./Button";
import { ApiError, ExpedienteTabItem } from "@interfaces/response";
import { BOFU_TABS_CONFIG, PATHS_EXPEDIENTE_V2, STATUS_CATALOG } from "@helpers/constantes";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import useStatusConfig from "@hooks/useStatusConfig";

const BASE = PATHS_EXPEDIENTE_V2.expediente.path;

interface TabEntry {
  code: string;
  queryParam: string;
  backLabel: string;
  nextLabel: string;
  tab: ExpedienteTabItem;
}

interface Props {
  currentTab: ExpedienteTabItem["code"];
  nextDisabled?: boolean;
  showRevision?: boolean;
}

export const StepActionsExpediente = ({ currentTab, nextDisabled = false, showRevision = false }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const expediente = useSelector((state: RootState) => state.expediente);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const { nextStatus } = useStatusConfig(STATUS_CATALOG.avance_expediente_digital.code);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [isLoadingRevision, setIsLoadingRevision] = useState(false);

  const orderedTabs: TabEntry[] = [
    {
      code: BOFU_TABS_CONFIG.tab_dictamen.code,
      queryParam: "dictamen",
      backLabel: "Volver a dictamen",
      nextLabel: "Continuar a dictamen",
      tab: expediente.dictamen,
    },
    {
      code: BOFU_TABS_CONFIG.tab_empresa.code,
      queryParam: "empresa",
      backLabel: "Volver a empresa",
      nextLabel: "Continuar a empresa",
      tab: expediente.empresa,
    },
    {
      code: BOFU_TABS_CONFIG.tab_garantia.code,
      queryParam: "garantia",
      backLabel: "Volver a garantía",
      nextLabel: "Continuar a garantía",
      tab: expediente.garantia,
    },
    {
      code: BOFU_TABS_CONFIG.tab_personas.code,
      queryParam: "personas",
      backLabel: "Volver a personas",
      nextLabel: "Continuar a personas",
      tab: expediente.personas,
    },
  ];

  const currentIndex = orderedTabs.findIndex((entry) => entry.code === currentTab);
  const prevEntry = orderedTabs
    .slice(0, currentIndex)
    .reverse()
    .find((entry) => entry.tab.is_active);
  const nextEntry = orderedTabs.slice(currentIndex + 1).find((entry) => entry.tab.is_active);

  const goTo = (queryParam: string) => navigate(`${BASE}?tab=${queryParam}`);

  const handleRevision = async () => {
    try {
      setIsLoadingRevision(true);

      if (!nextStatus) {
        throw new Error("No se encontró el siguiente estatus");
      }

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `BOFU - ${accountUser.email}`,
      }).unwrap();

      navigate(nextStatus.url || "");
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "StepActionsExpediente-revision" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoadingRevision(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 4, gap: 2 }}>
      {prevEntry ? (
        <Button tone="ghost" startIcon={<ChevronLeft fontSize="small" />} onClick={() => goTo(prevEntry.queryParam)}>
          {prevEntry.backLabel}
        </Button>
      ) : (
        <Box />
      )}

      <Box sx={{ display: "flex", gap: 2 }}>
        {nextEntry && (
          <Button
            tone="primary"
            onClick={() => goTo(nextEntry.queryParam)}
            disabled={nextEntry.tab.is_disabled || nextDisabled}
            withArrow
          >
            {nextEntry.nextLabel}
          </Button>
        )}

        {showRevision && (
          <Button
            tone="primary"
            onClick={handleRevision}
            disabled={isLoadingRevision || nextDisabled}
            startIcon={isLoadingRevision ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Enviar el expediente a revisión
          </Button>
        )}
      </Box>
    </Box>
  );
};
